import React, { useState, useEffect, useContext, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Minus, CalendarDays, CalendarPlus, Eye, Trash2, Pencil, Smile, Upload, Image, X, Stethoscope, ChevronDown } from "lucide-react";
import AdminSectionCard from "./AdminSectionCard";
import organizationsService from "../../services/organizationsService";
import eventsService from "../../services/eventsService";
import recognitionTypesService from "../../services/recognitionTypesService";
import workvivoService from "../../services/workvivoService";
import AuthContext from "../../context/AuthContext";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";
import { useTranslation, useFormat } from "../../i18n";

// Predefined gradient options (easy presets) — mirrors the Recognition Types editor.
// Labels are looked up by id (shared with the Recognition Types catalog keys).
const GRADIENT_PRESETS = [
  { from: "#3b82f6", to: "#06b6d4", id: "blue_cyan" },
  { from: "#22c55e", to: "#10b981", id: "green_emerald" },
  { from: "#eab308", to: "#f97316", id: "yellow_orange" },
  { from: "#a855f7", to: "#ec4899", id: "purple_pink" },
  { from: "#6366f1", to: "#3b82f6", id: "indigo_blue" },
  { from: "#f59e0b", to: "#eab308", id: "amber_yellow" },
  { from: "#ef4444", to: "#f43f5e", id: "red_rose" },
  { from: "#14b8a6", to: "#22c55e", id: "teal_green" },
  { from: "#ec4899", to: "#a855f7", id: "pink_purple" },
  { from: "#06b6d4", to: "#3b82f6", id: "cyan_blue" },
  { from: "#f97316", to: "#ef4444", id: "orange_red" },
  { from: "#84cc16", to: "#22c55e", id: "lime_green" },
];

// Color palette for visual selection
const COLOR_PALETTE = [
  ["#fecaca", "#fca5a5", "#f87171", "#ef4444", "#dc2626", "#b91c1c", "#991b1b"],
  ["#fed7aa", "#fdba74", "#fb923c", "#f97316", "#ea580c", "#c2410c", "#9a3412"],
  ["#fef08a", "#fde047", "#facc15", "#eab308", "#ca8a04", "#a16207", "#854d0e"],
  ["#bbf7d0", "#86efac", "#4ade80", "#22c55e", "#16a34a", "#15803d", "#166534"],
  ["#99f6e4", "#5eead4", "#2dd4bf", "#14b8a6", "#0d9488", "#0f766e", "#115e59"],
  ["#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8", "#1e40af"],
  ["#c7d2fe", "#a5b4fc", "#818cf8", "#6366f1", "#4f46e5", "#4338ca", "#3730a3"],
  ["#e9d5ff", "#d8b4fe", "#c084fc", "#a855f7", "#9333ea", "#7e22ce", "#6b21a8"],
  ["#fbcfe8", "#f9a8d4", "#f472b6", "#ec4899", "#db2777", "#be185d", "#9d174d"],
  ["#e5e7eb", "#d1d5db", "#9ca3af", "#6b7280", "#4b5563", "#374151", "#1f2937"],
];

// Extended emoji options organized by category. Labels are looked up by id.
const EMOJI_CATEGORIES = {
  people: ["🤝", "💪", "👏", "🙌", "👍", "✋", "🤲", "👑"],
  objects: ["💼", "💡", "🎯", "⭐", "🏆", "🎖️", "💎", "🔑"],
  celebration: ["🎉", "🎊", "✨", "🌟", "🔥", "💥", "⚡", "🎁"],
  hearts: ["❤️", "💜", "💙", "💚", "🧡", "💛", "🤍", "💖"],
  nature: ["🚀", "🌈", "☀️", "🌙", "🦁", "🦅", "🐝", "🌸"],
  sports: ["🥇", "🥈", "🥉", "🎮", "🎨", "🎵", "📚", "💻"],
};

// Quick access emojis (shown first)
const QUICK_EMOJIS = ["🤝", "💼", "💡", "👑", "🎯", "⭐", "🚀", "💪", "🏆", "🎉", "❤️", "🔥", "✨", "🌟", "💎", "🎖️"];

const PillToggle = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    aria-pressed={checked}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
      checked ? "bg-purple-600" : "bg-slate-300"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const extractHexColor = (value) => {
  if (!value) return null;
  const match = String(value).match(/#([0-9a-fA-F]{3,8})/);
  return match ? `#${match[1]}` : null;
};

const isCustomIconUrl = (value) =>
  typeof value === "string" && value.startsWith("/api/v1/recognition-type-icons/");

const toImportantDatesSettings = (org) => ({
  pastDays: org.important_dates_past_days ?? org.important_dates_window_days ?? 7,
  futureDays: org.important_dates_future_days ?? org.important_dates_window_days ?? 7,
  eventRetentionDays: org.event_retention_days ?? 365,
  enabled: org.important_dates_enabled ?? true,
  showBirthdays: org.show_birthdays ?? false,
  showAnniversaries: org.show_work_anniversaries ?? true,
  showEvents: org.show_events ?? true,
  eventsUseNominations: org.events_use_nominations ?? false,
});

// Turns the diagnostics payload into plain-English findings, so an admin does
// not have to interpret raw counts to work out why the column is empty.
const diagnosticFindings = (data, t) => {
  const findings = [];
  const settings = data.org_settings || {};
  const db = data.database || {};
  const wv = data.workvivo || {};

  if (!settings.important_dates_enabled) {
    findings.push(["error", t("important_dates.diagnostics.column_off")]);
  }
  if (!settings.show_birthdays) {
    findings.push(["warn", t("important_dates.diagnostics.birthdays_hidden")]);
  }

  if (wv.configured === false) {
    findings.push(["error", t("important_dates.diagnostics.no_integration")]);
  } else if (wv.is_enabled === false) {
    findings.push(["error", t("important_dates.diagnostics.integration_disabled")]);
  }

  const users = wv.users;
  if (users && !users.ok) {
    findings.push([
      "error",
      t("important_dates.diagnostics.users_error", {
        status: users.http_status || t("important_dates.diagnostics.an_error"),
        error: users.error || t("important_dates.diagnostics.unknown"),
      }),
    ]);
  } else if (users) {
    if (users.parsable_birth_dates === 0 && users.parsable_hire_dates === 0) {
      findings.push([
        "error",
        t("important_dates.diagnostics.users_no_dates", { count: users.sampled }),
      ]);
    } else {
      findings.push([
        "ok",
        t("important_dates.diagnostics.users_ok", {
          birth: users.parsable_birth_dates,
          hire: users.parsable_hire_dates,
          sampled: users.sampled,
        }),
      ]);
    }
  }

  const events = wv.events;
  if (events && !events.ok) {
    const httpError = events.http_status
      ? t("important_dates.diagnostics.http_error", { status: events.http_status })
      : t("important_dates.diagnostics.http_error_unknown");
    findings.push([
      "error",
      t("important_dates.diagnostics.events_error", { error: events.error || httpError }),
    ]);
  } else if (events) {
    findings.push(["ok", t("important_dates.diagnostics.events_ok", { count: events.sampled })]);
  }

  if (db.users_with_birth_date === 0 && db.users_with_hire_date === 0) {
    findings.push(["warn", t("important_dates.diagnostics.db_no_dates")]);
  }
  if (db.events_in_window === 0 && db.events_total > 0) {
    const range = t("important_dates.diagnostics.window_range", {
      past: settings.important_dates_past_days ?? settings.important_dates_window_days,
      future: settings.important_dates_future_days ?? settings.important_dates_window_days,
    });
    findings.push([
      "warn",
      t("important_dates.diagnostics.events_outside_window", {
        count: db.events_total,
        range,
        start: db.window?.start,
        end: db.window?.end,
      }),
    ]);
  }

  return findings;
};

const FINDING_STYLES = {
  ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  warn: "bg-amber-50 text-amber-800 border-amber-200",
  error: "bg-red-50 text-red-800 border-red-200",
};

const ImportantDatesDiagnostics = ({ data }) => {
  const { t } = useTranslation("admin");
  const findings = diagnosticFindings(data, t);
  const users = data.workvivo?.users;

  return (
    <div className="mt-3 p-3 sm:p-4 rounded-2xl border border-slate-200 bg-slate-50 space-y-3">
      <h4 className="text-sm font-semibold text-slate-800">{t("important_dates.diagnostics.title")}</h4>

      <div className="space-y-2">
        {findings.length === 0 ? (
          <p className="text-xs text-slate-500">{t("important_dates.diagnostics.nothing_wrong")}</p>
        ) : (
          findings.map(([level, message], idx) => (
            <p
              key={idx}
              className={`text-xs px-3 py-2 rounded-xl border ${FINDING_STYLES[level]}`}
            >
              {message}
            </p>
          ))
        )}
      </div>

      {users?.record_keys?.length > 0 && (
        <details className="text-xs text-slate-600">
          <summary className="cursor-pointer font-medium">
            {t("important_dates.diagnostics.fields_returned")}
          </summary>
          <p className="mt-2 font-mono break-words text-[11px] text-slate-500">
            {users.record_keys.join(", ")}
          </p>
        </details>
      )}

      <details className="text-xs text-slate-600">
        <summary className="cursor-pointer font-medium">{t("important_dates.diagnostics.raw")}</summary>
        <pre className="mt-2 p-2 bg-white rounded-xl border border-slate-200 overflow-x-auto text-[11px]">
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
};

// Editor for an occasion type's icon + gradient colors. Mirrors the Recognition
// Types editor (emoji/custom-icon picker + full gradient picker + preview) so
// occasion styling feels identical — except the name/title is fixed and cannot
// be changed here (these are system occasion types).
const OccasionStyleModal = ({ type, saving, selectedOrgId, onClose, onSave }) => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { t } = useTranslation("admin");

  const presetLabels = {
    blue_cyan: t("recognition_types.presets.blue_cyan"),
    green_emerald: t("recognition_types.presets.green_emerald"),
    yellow_orange: t("recognition_types.presets.yellow_orange"),
    purple_pink: t("recognition_types.presets.purple_pink"),
    indigo_blue: t("recognition_types.presets.indigo_blue"),
    amber_yellow: t("recognition_types.presets.amber_yellow"),
    red_rose: t("recognition_types.presets.red_rose"),
    teal_green: t("recognition_types.presets.teal_green"),
    pink_purple: t("recognition_types.presets.pink_purple"),
    cyan_blue: t("recognition_types.presets.cyan_blue"),
    orange_red: t("recognition_types.presets.orange_red"),
    lime_green: t("recognition_types.presets.lime_green"),
  };
  const emojiCategoryLabels = {
    people: t("recognition_types.emoji_categories.people"),
    objects: t("recognition_types.emoji_categories.objects"),
    celebration: t("recognition_types.emoji_categories.celebration"),
    hearts: t("recognition_types.emoji_categories.hearts"),
    nature: t("recognition_types.emoji_categories.nature"),
    sports: t("recognition_types.emoji_categories.sports"),
  };

  const startingIcon = type.icon || type.emoji || "⭐";
  const [icon, setIcon] = useState(startingIcon);
  const [colorFrom, setColorFrom] = useState(extractHexColor(type.color_from) || "#a855f7");
  const [colorTo, setColorTo] = useState(extractHexColor(type.color_to) || "#3b82f6");

  const [iconTab, setIconTab] = useState(isCustomIconUrl(startingIcon) ? "custom" : "emojis");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pickingColor, setPickingColor] = useState(null); // null | 'from' | 'to'

  const label = type.name === "Celebration" ? t("important_dates.occasion_modal.event_name") : type.name;

  // Custom icons query (shared with the Recognition Types tab)
  const { data: customIcons = [], refetch: refetchCustomIcons } = useQuery({
    queryKey: ["recognition-type-custom-icons", selectedOrgId],
    queryFn: () => recognitionTypesService.getCustomIcons(selectedOrgId),
    enabled: !!selectedOrgId,
  });

  const uploadIconMutation = useMutation({
    mutationFn: (file) => recognitionTypesService.uploadCustomIcon(selectedOrgId, file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["recognition-type-custom-icons", selectedOrgId] });
      toast.success(t("recognition_types.editor.icon_uploaded"));
      setIcon(data.url);
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const deleteIconMutation = useMutation({
    mutationFn: (iconId) => recognitionTypesService.deleteCustomIcon(iconId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recognition-type-custom-icons", selectedOrgId] });
      toast.success(t("recognition_types.editor.icon_deleted"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const handleIconUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/x-icon"];
    const validExtensions = [".png", ".ico"];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));

    if (!validTypes.includes(file.type) && !validExtensions.includes(ext)) {
      toast.error(t("recognition_types.editor.invalid_icon_type"));
      return;
    }
    if (file.size > 512 * 1024) {
      toast.error(t("recognition_types.editor.icon_too_large"));
      return;
    }
    uploadIconMutation.mutate(file);
    e.target.value = "";
  };

  const handleColorSelect = (color) => {
    if (pickingColor === "from") setColorFrom(color);
    else if (pickingColor === "to") setColorTo(color);
    setPickingColor(null);
  };

  const handleGradientPreset = (preset) => {
    setColorFrom(preset.from);
    setColorTo(preset.to);
    setPickingColor(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-3 sm:p-6 border-b border-slate-200">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">
            {t("important_dates.occasion_modal.title", { name: label })}
          </h3>
        </div>

        <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
          {/* Name (fixed — system occasion type, not editable) */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
              {t("important_dates.occasion_modal.name_label")}
            </label>
            <div className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base border border-slate-200 bg-slate-50 text-slate-500 rounded-full cursor-not-allowed">
              {label}
            </div>
            <p className="text-[11px] sm:text-xs text-slate-400 mt-1">
              {t("important_dates.occasion_modal.system_type_hint")}
            </p>
          </div>

          {/* Icon Picker */}
          <div>
            <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2 block">{t("recognition_types.editor.icon_label")}</label>

            {/* Tab Buttons */}
            <div className="flex gap-2 mb-2 sm:mb-3">
              <button
                type="button"
                onClick={() => setIconTab("emojis")}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  iconTab === "emojis"
                    ? "bg-purple-100 text-purple-700 ring-2 ring-purple-500"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Smile className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {t("recognition_types.editor.icons_tab")}
              </button>
              <button
                type="button"
                onClick={() => { setIconTab("custom"); refetchCustomIcons(); }}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  iconTab === "custom"
                    ? "bg-purple-100 text-purple-700 ring-2 ring-purple-500"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Image className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {t("recognition_types.editor.custom_icons_tab")}
              </button>
            </div>

            {/* Emojis Tab */}
            {iconTab === "emojis" && (
              <div>
                {/* Quick Emojis */}
                <div className="grid grid-cols-8 gap-1 sm:gap-2 mb-2">
                  {QUICK_EMOJIS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setIcon(e)}
                      className={`aspect-square flex items-center justify-center text-lg sm:text-2xl rounded-full transition-all ${
                        icon === e
                          ? "bg-purple-100 ring-2 ring-purple-500"
                          : "hover:bg-slate-100"
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                </div>

                {/* More Emojis Toggle */}
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="flex items-center gap-1 text-xs sm:text-sm text-purple-600 hover:text-purple-700 mb-2"
                >
                  <Smile className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {showEmojiPicker ? t("recognition_types.editor.show_less") : t("recognition_types.editor.more_emojis")}
                </button>

                {/* Extended Emoji Picker */}
                {showEmojiPicker && (
                  <div className="border border-slate-200 rounded-2xl p-2 sm:p-3 mt-2 max-h-60 overflow-y-auto">
                    {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                      <div key={category} className="mb-3">
                        <div className="text-[11px] sm:text-xs font-medium text-slate-500 mb-1">{emojiCategoryLabels[category] || category}</div>
                        <div className="grid grid-cols-8 gap-1">
                          {emojis.map((e) => (
                            <button
                              key={`${category}-${e}`}
                              type="button"
                              onClick={() => setIcon(e)}
                              className={`aspect-square flex items-center justify-center text-base sm:text-xl rounded transition-all ${
                                icon === e
                                  ? "bg-purple-100 ring-2 ring-purple-500"
                                  : "hover:bg-slate-100"
                              }`}
                            >
                              {e}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Custom emoji input */}
                <div className="mt-2">
                  <input
                    type="text"
                    value={isCustomIconUrl(icon) ? "" : icon}
                    onChange={(e) => setIcon(e.target.value.slice(0, 2))}
                    placeholder={t("recognition_types.editor.emoji_placeholder")}
                    className="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center text-lg sm:text-2xl"
                    maxLength={2}
                  />
                </div>
              </div>
            )}

            {/* Custom Icons Tab */}
            {iconTab === "custom" && (
              <div>
                {/* Upload Button */}
                <label className="flex items-center justify-center gap-2 w-full p-3 mb-3 border-2 border-dashed border-slate-300 rounded-2xl text-slate-600 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors cursor-pointer">
                  <Upload className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {uploadIconMutation.isPending ? t("recognition_types.editor.uploading") : t("recognition_types.editor.upload_custom_icon")}
                  </span>
                  <input
                    type="file"
                    accept=".png,.ico"
                    onChange={handleIconUpload}
                    disabled={uploadIconMutation.isPending}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-slate-500 mb-3">{t("recognition_types.editor.format_hint")}</p>

                {/* Custom Icons Gallery */}
                {customIcons.length > 0 ? (
                  <div className="grid grid-cols-6 gap-2">
                    {customIcons.map((ci) => (
                      <div key={ci.id} className="relative group">
                        <button
                          type="button"
                          onClick={() => setIcon(ci.url)}
                          className={`w-full aspect-square rounded-xl border-2 p-1.5 flex items-center justify-center transition-all ${
                            icon === ci.url
                              ? "border-purple-500 bg-purple-50 ring-2 ring-purple-200"
                              : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                          title={ci.filename}
                        >
                          <img src={ci.url} alt={ci.filename} className="w-8 h-8 object-contain" />
                        </button>
                        <button
                          type="button"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            if (icon === ci.url) setIcon("⭐");
                            deleteIconMutation.mutate(ci.id);
                          }}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                          title={t("recognition_types.editor.delete_icon")}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-400 text-sm">
                    {t("recognition_types.editor.no_custom_icons")}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2 sm:mb-3">{t("recognition_types.editor.gradient_label")}</label>

            {/* Current Gradient Preview with clickable selectors */}
            <div className="mb-3 sm:mb-4">
              <div className="flex items-center gap-2 sm:gap-3 mb-2">
                {/* Start Color Selector */}
                <button
                  type="button"
                  onClick={() => setPickingColor(pickingColor === "from" ? null : "from")}
                  className={`flex-1 min-w-0 p-2 sm:p-3 rounded-full border-2 transition-all ${
                    pickingColor === "from"
                      ? "border-purple-500 ring-2 ring-purple-200"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                    <div
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-2xl shadow-inner border border-slate-200 flex-shrink-0"
                      style={{ backgroundColor: colorFrom }}
                    />
                    <div className="text-left min-w-0 flex-1">
                      <div className="text-[10px] sm:text-xs text-slate-500 truncate">{t("recognition_types.editor.start")}</div>
                      <div className="text-[11px] sm:text-sm font-medium text-slate-700 whitespace-nowrap truncate">
                        {pickingColor === "from" ? t("recognition_types.editor.pick_below") : t("recognition_types.editor.click_to_change")}
                      </div>
                    </div>
                  </div>
                </button>

                {/* Arrow */}
                <div className="text-slate-400 flex-shrink-0">→</div>

                {/* End Color Selector */}
                <button
                  type="button"
                  onClick={() => setPickingColor(pickingColor === "to" ? null : "to")}
                  className={`flex-1 min-w-0 p-2 sm:p-3 rounded-full border-2 transition-all ${
                    pickingColor === "to"
                      ? "border-purple-500 ring-2 ring-purple-200"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                    <div
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-2xl shadow-inner border border-slate-200 flex-shrink-0"
                      style={{ backgroundColor: colorTo }}
                    />
                    <div className="text-left min-w-0 flex-1">
                      <div className="text-[10px] sm:text-xs text-slate-500 truncate">{t("recognition_types.editor.end")}</div>
                      <div className="text-[11px] sm:text-sm font-medium text-slate-700 whitespace-nowrap truncate">
                        {pickingColor === "to" ? t("recognition_types.editor.pick_below") : t("recognition_types.editor.click_to_change")}
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Gradient Preview Bar */}
              <div
                className="h-8 sm:h-10 rounded-2xl shadow-inner border border-slate-200"
                style={{ background: `linear-gradient(to right, ${colorFrom}, ${colorTo})` }}
              />
            </div>

            {/* Color Palette (shown when picking a color) */}
            {pickingColor && (
              <div className="mb-4 p-3 border border-purple-200 rounded-2xl bg-purple-50/50">
                <div className="text-xs font-medium text-purple-700 mb-2">
                  {pickingColor === "from" ? t("recognition_types.editor.select_start_color") : t("recognition_types.editor.select_end_color")}
                </div>
                {/* Eyedropper + Hex input */}
                <div className="flex items-center gap-2 mb-3">
                  <label className="relative w-10 h-10 flex-shrink-0 cursor-pointer" title={t("recognition_types.editor.eyedropper_title")}>
                    <div
                      className="w-10 h-10 rounded-full border-2 border-slate-200 shadow-inner"
                      style={{ backgroundColor: pickingColor === "from" ? colorFrom : colorTo }}
                    />
                    <input
                      type="color"
                      value={pickingColor === "from" ? colorFrom : colorTo}
                      onChange={(e) => {
                        const c = e.target.value;
                        if (pickingColor === "from") setColorFrom(c);
                        else setColorTo(c);
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </label>
                  <input
                    type="text"
                    key={`${pickingColor}-${pickingColor === "from" ? colorFrom : colorTo}`}
                    defaultValue={pickingColor === "from" ? colorFrom : colorTo}
                    onChange={(e) => {
                      let val = e.target.value.trim();
                      if (!val.startsWith("#")) val = "#" + val;
                      if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                        if (pickingColor === "from") setColorFrom(val);
                        else setColorTo(val);
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
                    placeholder="#000000"
                    maxLength={7}
                  />
                </div>
                <div className="space-y-1">
                  {COLOR_PALETTE.map((row, rowIndex) => (
                    <div key={rowIndex} className="flex gap-1">
                      {row.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => handleColorSelect(color)}
                          className={`flex-1 h-7 rounded transition-all hover:scale-110 hover:z-10 ${
                            (pickingColor === "from" && colorFrom === color) ||
                            (pickingColor === "to" && colorTo === color)
                              ? "ring-2 ring-purple-500 ring-offset-1"
                              : ""
                          }`}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Gradient Presets */}
            <div>
              <div className="text-xs font-medium text-slate-500 mb-2">{t("recognition_types.editor.quick_presets")}</div>
              <div className="grid grid-cols-4 gap-2">
                {GRADIENT_PRESETS.map((preset, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleGradientPreset(preset)}
                    className={`h-10 rounded-full transition-all ${
                      colorFrom === preset.from && colorTo === preset.to
                        ? "ring-2 ring-offset-2 ring-purple-500"
                        : "hover:scale-105"
                    }`}
                    style={{ background: `linear-gradient(to right, ${preset.from}, ${preset.to})` }}
                    title={presetLabels[preset.id] || preset.id}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t("recognition_types.editor.preview")}
            </label>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-md"
                style={{ background: `linear-gradient(to right, ${colorFrom}, ${colorTo})` }}
              >
                {isCustomIconUrl(icon) ? (
                  <img src={icon} alt="" className="w-7 h-7 object-contain" />
                ) : (
                  icon
                )}
              </div>
              <div className="font-medium text-slate-900">{label}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="button"
              onClick={() => onSave({ emoji: icon, colorFrom, colorTo })}
              disabled={saving}
              className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm org-bg-primary org-btn text-white rounded-full transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {saving ? t("recognition_types.editor.saving") : t("common:actions.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MAX_WINDOW_DAYS = 365;

// Local-midnight date helpers. `new Date("YYYY-MM-DD")` parses as UTC, which
// shifts the day either side of midnight for most timezones — appending a time
// keeps these comparisons in the admin's own day.
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const parseISODate = (value) => {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const addDays = (base, days) => {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
};

// How long until the sync prunes a finished event, given the retention setting.
const pruneLabel = (event, retentionDays, t) => {
  if (!retentionDays) return t("important_dates.prune.kept_indefinitely");
  if (event.source !== "workvivo") return t("important_dates.prune.kept_custom");
  const ends = parseISODate(event.end_date || event.event_date);
  if (!ends) return null;
  const daysLeft = Math.ceil(
    (addDays(ends, retentionDays) - startOfToday()) / 86400000
  );
  if (daysLeft <= 0) return t("important_dates.prune.removed_next_sync");
  if (daysLeft === 1) return t("important_dates.prune.removed_tomorrow");
  return t("important_dates.prune.removed_in_days", { count: daysLeft });
};

// One direction of the important-dates window. Used twice (past and future).
const DayRangeStepper = ({ label, value, onChange, max = MAX_WINDOW_DAYS }) => {
  const { t } = useTranslation("admin");
  const clamp = (n) => Math.max(0, Math.min(max, n));
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={t("important_dates.stepper.decrease", { label })}
          onClick={() => onChange(clamp(value - 1))}
          disabled={value <= 0}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
        >
          <Minus className="w-5 h-5" />
        </button>
        <input
          type="number"
          min="0"
          max={max}
          aria-label={label}
          value={value}
          onChange={(e) => onChange(clamp(parseInt(e.target.value, 10) || 0))}
          className="flex-1 min-w-0 px-3 py-2 text-center border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <button
          type="button"
          aria-label={t("important_dates.stepper.increase", { label })}
          onClick={() => onChange(clamp(value + 1))}
          disabled={value >= max}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

/**
 * `embedded` renders this inside the Widgets tab's Important Dates card: the
 * page padding and the page heading are dropped (the card supplies both) and
 * the nested panels lose their drop shadow so they read as sections of the
 * card rather than as free-floating cards on top of one.
 */
const ImportantDatesSection = ({ embedded = false }) => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { t } = useTranslation("admin");
  const fmt = useFormat();
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.is_superuser;

  const [selectedOrgId, setSelectedOrgId] = useState(null);

  const [importantDates, setImportantDates] = useState({
    pastDays: 7,
    futureDays: 7,
    eventRetentionDays: 365,
    enabled: true,
    showBirthdays: false,
    showAnniversaries: true,
    showEvents: true,
    eventsUseNominations: false,
  });
  const [newEvent, setNewEvent] = useState({ name: "", description: "", event_date: "", end_date: "" });
  const [editingOccasion, setEditingOccasion] = useState(null);
  const [showPastEvents, setShowPastEvents] = useState(false);
  // Expanded by default: collapsing is opt-in, so nothing an admin
  // expects to see disappears on first load.
  const [eventsExpanded, setEventsExpanded] = useState(true);

  const { data: organizations = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationsService.getAllOrganizations(),
  });

  // Org admins are pinned to their own org; super admins start on the first one.
  useEffect(() => {
    if (selectedOrgId) return;
    if (!isSuperAdmin && user?.organization_id) {
      setSelectedOrgId(user.organization_id);
    } else if (organizations.length > 0) {
      setSelectedOrgId(organizations[0].id);
    }
  }, [organizations, selectedOrgId, isSuperAdmin, user?.organization_id]);

  const { data: selectedOrg } = useQuery({
    queryKey: ["organization", selectedOrgId],
    queryFn: () => organizationsService.getOrganizationById(selectedOrgId),
    enabled: !!selectedOrgId,
  });

  useEffect(() => {
    if (selectedOrg) setImportantDates(toImportantDatesSettings(selectedOrg));
  }, [selectedOrg]);

  const { data: orgEvents = [], isLoading: isLoadingEvents } = useQuery({
    queryKey: ["org-events", selectedOrgId],
    queryFn: () => eventsService.list(false, selectedOrgId),
    enabled: !!selectedOrgId,
  });

  const { data: recognitionTypes = [] } = useQuery({
    queryKey: ["recognition-types", selectedOrgId],
    queryFn: () => recognitionTypesService.getByOrganization(selectedOrgId, true),
    enabled: !!selectedOrgId,
  });
  const systemTypes = recognitionTypes.filter((t) => t.is_private);

  const updateImportantDatesMutation = useMutation({
    mutationFn: () =>
      organizationsService.updateOrganization(selectedOrgId, {
        important_dates_past_days: importantDates.pastDays,
        important_dates_future_days: importantDates.futureDays,
        event_retention_days: importantDates.eventRetentionDays,
        important_dates_enabled: importantDates.enabled,
        show_birthdays: importantDates.showBirthdays,
        show_work_anniversaries: importantDates.showAnniversaries,
        show_events: importantDates.showEvents,
        events_use_nominations: importantDates.eventsUseNominations,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["organization", selectedOrgId] });
      toast.success(t("important_dates.toasts.settings_saved"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  // Birthdays, anniversaries and Workvivo events only appear once the sync has
  // written the underlying fields, and there are several silent reasons it
  // might not have. This reports which one applies.
  const [diagnostics, setDiagnostics] = useState(null);
  const diagnosticsMutation = useMutation({
    mutationFn: () => workvivoService.getImportantDatesDiagnostics(selectedOrgId),
    onSuccess: (data) => setDiagnostics(data),
    onError: (error) => toast.error(formatError(error)),
  });

  const createEventMutation = useMutation({
    mutationFn: (data) => eventsService.create(data, selectedOrgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-events", selectedOrgId] });
      toast.success(t("important_dates.toasts.event_created"));
      setNewEvent({ name: "", description: "", event_date: "", end_date: "" });
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const deleteEventMutation = useMutation({
    mutationFn: (eventId) => eventsService.delete(eventId, selectedOrgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-events", selectedOrgId] });
      toast.success(t("important_dates.toasts.event_removed"));
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const toggleEventNominationsMutation = useMutation({
    mutationFn: ({ eventId, useNominations }) =>
      eventsService.update(eventId, { use_nominations: useNominations }, selectedOrgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-events", selectedOrgId] });
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const updateOccasionMutation = useMutation({
    mutationFn: ({ type, emoji, colorFrom, colorTo }) =>
      recognitionTypesService.update(type.id, {
        name: type.name,
        value: type.name,
        description: type.description || null,
        icon: emoji,
        color_from: `from-[${colorFrom}]`,
        color_to: `to-[${colorTo}]`,
        organization_id: selectedOrgId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recognition-types", selectedOrgId] });
      toast.success(t("important_dates.toasts.occasion_saved"));
      setEditingOccasion(null);
    },
    onError: (error) => toast.error(formatError(error)),
  });

  const handleCreateEvent = () => {
    if (!newEvent.name.trim() || !newEvent.event_date) {
      toast.error(t("important_dates.toasts.name_and_date_required"));
      return;
    }
    createEventMutation.mutate({
      name: newEvent.name.trim(),
      description: newEvent.description.trim() || null,
      event_date: newEvent.event_date,
      end_date: newEvent.end_date || null,
    });
  };

  // Events that finished before the window starts no longer appear in the feed.
  // A tenant can easily have a hundred of them, which buries the handful an
  // admin actually manages, so they are split out and collapsed by default.
  const { currentEvents, pastEvents } = useMemo(() => {
    const today = startOfToday();
    const windowStart = addDays(today, -(importantDates.pastDays || 0));
    const current = [];
    const past = [];
    for (const event of orgEvents) {
      const ends = parseISODate(event.end_date || event.event_date);
      if (ends && ends < windowStart) past.push(event);
      else current.push(event);
    }
    return { currentEvents: current, pastEvents: past };
  }, [orgEvents, importantDates.pastDays]);

  // Event dates arrive as "YYYY-MM-DD"; render them in the admin's locale.
  const eventDate = (value) => fmt.date(parseISODate(value)) || value || "";

  return (
    <div className={embedded ? "space-y-4 sm:space-y-6" : "p-3 sm:p-6 space-y-4 sm:space-y-6"}>
      {/* Header */}
      {(!embedded || isSuperAdmin) && (
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {!embedded && (
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-slate-800">{t("important_dates.title")}</h1>
            <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1">
              {t("important_dates.subtitle")}
            </p>
          </div>
        )}
        {isSuperAdmin && (
          <select
            value={selectedOrgId || ""}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className="px-3 sm:px-4 py-1.5 sm:py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xs sm:text-sm max-w-[160px] sm:max-w-none flex-shrink-0"
            disabled={isLoadingOrgs}
          >
            {isLoadingOrgs ? (
              <option>{t("important_dates.org_loading")}</option>
            ) : organizations.length === 0 ? (
              <option>{t("important_dates.no_organizations")}</option>
            ) : (
              organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))
            )}
          </select>
        )}
      </div>
      )}

      {/* Master switch. Everything below it is a section of its own so the
          settings stop running together in one flat card. */}
      <AdminSectionCard
        elevated={!embedded}
        icon={CalendarDays}
        title={embedded ? t("important_dates.feed_column") : t("important_dates.title")}
        description={
          embedded
            ? t("important_dates.master_description_embedded")
            : t("important_dates.master_description")
        }
        action={
          <div className="flex items-center gap-2">
            <span className={`text-xs sm:text-sm font-medium ${importantDates.enabled ? "text-purple-700" : "text-slate-500"}`}>
              {importantDates.enabled ? t("important_dates.enabled") : t("important_dates.disabled")}
            </span>
            <PillToggle
              checked={!!importantDates.enabled}
              onChange={() =>
                setImportantDates((prev) => ({ ...prev, enabled: !prev.enabled }))
              }
            />
          </div>
        }
      />

      <div className={`space-y-4 sm:space-y-6 ${!importantDates.enabled ? "opacity-50 pointer-events-none" : ""}`}>
          {/* Date window — past and future are set independently, so an org can
              show a week of history but a month of upcoming events. */}
          <AdminSectionCard
            elevated={!embedded}
            icon={CalendarDays}
            title={t("important_dates.timing.title")}
            description={t("important_dates.timing.description")}
          >
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t("important_dates.timing.window_label")}
                </label>
                <p className="text-xs text-slate-500 mb-3">
                  {t("important_dates.timing.window_hint")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DayRangeStepper
                    label={t("important_dates.timing.past_days")}
                    value={importantDates.pastDays}
                    onChange={(v) => setImportantDates((prev) => ({ ...prev, pastDays: v }))}
                  />
                  <DayRangeStepper
                    label={t("important_dates.timing.future_days")}
                    value={importantDates.futureDays}
                    onChange={(v) => setImportantDates((prev) => ({ ...prev, futureDays: v }))}
                  />
                </div>
              </div>

              {/* Retention for synced events */}
              <div className="pt-5 border-t border-slate-200">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t("important_dates.timing.retention_label")}
                </label>
                <p className="text-xs text-slate-500 mb-3">
                  {t("important_dates.timing.retention_hint")}
                </p>
                <div className="sm:max-w-xs">
                  <DayRangeStepper
                    label={t("important_dates.timing.retention_days")}
                    value={importantDates.eventRetentionDays}
                    max={3650}
                    onChange={(v) =>
                      setImportantDates((prev) => ({ ...prev, eventRetentionDays: v }))
                    }
                  />
                </div>
                {importantDates.eventRetentionDays === 0 && (
                  <p className="text-xs text-amber-700 mt-2">
                    {t("important_dates.timing.pruning_off")}
                  </p>
                )}
              </div>
            </div>
          </AdminSectionCard>

          {/* Feature toggles */}
          <AdminSectionCard
            elevated={!embedded}
            icon={Eye}
            title={t("important_dates.show.title")}
            description={t("important_dates.show.description")}
          >
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 p-3 sm:p-4 rounded-2xl border border-slate-200 bg-white">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{t("important_dates.show.birthdays_label")}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {t("important_dates.show.birthdays_hint")}
                </p>
              </div>
              <PillToggle
                checked={!!importantDates.showBirthdays}
                onChange={() =>
                  setImportantDates((prev) => ({ ...prev, showBirthdays: !prev.showBirthdays }))
                }
              />
            </div>

            <div className="flex items-center justify-between gap-3 p-3 sm:p-4 rounded-2xl border border-slate-200 bg-white">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{t("important_dates.show.anniversaries_label")}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {t("important_dates.show.anniversaries_hint")}
                </p>
              </div>
              <PillToggle
                checked={!!importantDates.showAnniversaries}
                onChange={() =>
                  setImportantDates((prev) => ({ ...prev, showAnniversaries: !prev.showAnniversaries }))
                }
              />
            </div>

            <div className="flex items-center justify-between gap-3 p-3 sm:p-4 rounded-2xl border border-slate-200 bg-white">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{t("important_dates.show.events_label")}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {t("important_dates.show.events_hint")}
                </p>
              </div>
              <PillToggle
                checked={!!importantDates.showEvents}
                onChange={() =>
                  setImportantDates((prev) => ({ ...prev, showEvents: !prev.showEvents }))
                }
              />
            </div>

            <div className="flex items-center justify-between gap-3 p-3 sm:p-4 rounded-2xl border border-slate-200 bg-white">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{t("important_dates.show.nominations_label")}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {t("important_dates.show.nominations_hint")}
                </p>
              </div>
              <PillToggle
                checked={!!importantDates.eventsUseNominations}
                onChange={() =>
                  setImportantDates((prev) => ({
                    ...prev,
                    eventsUseNominations: !prev.eventsUseNominations,
                  }))
                }
              />
            </div>
          </div>
          </AdminSectionCard>

          {/* Occasion styling: icon + colors of the system recognition types */}
          <AdminSectionCard
            elevated={!embedded}
            icon={Pencil}
            title={t("important_dates.occasions.title")}
            description={t("important_dates.occasions.description")}
          >
            <div className="space-y-2">
              {systemTypes.map((type) => {
                const displayIcon = type.icon || type.emoji || "⭐";
                const from = extractHexColor(type.color_from) || "#a855f7";
                const to = extractHexColor(type.color_to) || "#3b82f6";
                return (
                  <div
                    key={type.id}
                    className="flex items-center justify-between gap-2 p-3 rounded-2xl border border-slate-200 bg-white"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl flex items-center justify-center text-lg sm:text-xl shadow-md flex-shrink-0"
                        style={{ background: `linear-gradient(to right, ${from}, ${to})` }}
                      >
                        {isCustomIconUrl(displayIcon) ? (
                          <img src={displayIcon} alt="" className="w-6 h-6 object-contain" />
                        ) : (
                          displayIcon
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm sm:text-base font-medium text-slate-900 truncate">
                          {type.name === "Celebration" ? (
                            <span className="italic">{t("important_dates.occasions.event_name_placeholder")}</span>
                          ) : (
                            type.name
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          {type.name === "Celebration"
                            ? t("important_dates.occasions.celebration_usage")
                            : t("important_dates.occasions.type_usage", { type: type.name.toLowerCase() })}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setEditingOccasion(type)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors flex-shrink-0"
                      title={t("important_dates.occasions.edit_title", { name: type.name })}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      {t("important_dates.occasions.edit")}
                    </button>
                  </div>
                );
              })}
              {systemTypes.length === 0 && (
                <p className="text-sm text-slate-400">{t("important_dates.occasions.loading")}</p>
              )}
            </div>
          </AdminSectionCard>

          {/* Save applies to every section above, so it sits below all of them. */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => updateImportantDatesMutation.mutate()}
              disabled={updateImportantDatesMutation.isPending}
              className="w-full sm:w-auto text-center px-4 py-2.5 sm:py-2 text-sm org-bg-primary org-btn text-white rounded-full transition-colors disabled:opacity-50"
            >
              {updateImportantDatesMutation.isPending ? t("important_dates.saving") : t("important_dates.save_changes")}
            </button>
            <button
              onClick={() => diagnosticsMutation.mutate()}
              disabled={diagnosticsMutation.isPending}
              className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-4 py-2.5 sm:py-2 text-sm font-medium text-slate-600 border border-slate-300 hover:bg-slate-50 rounded-full transition-colors disabled:opacity-50"
              title={t("important_dates.diagnostics_title")}
            >
              <Stethoscope className="w-4 h-4" />
              {diagnosticsMutation.isPending ? t("important_dates.checking") : t("important_dates.run_diagnostics")}
            </button>
          </div>

          {diagnostics && <ImportantDatesDiagnostics data={diagnostics} />}

          {/* Custom Events. Saved individually, so it sits below the Save row
              rather than looking like part of what that button applies to. */}
          <AdminSectionCard
            elevated={!embedded}
            icon={CalendarPlus}
            title={
              <span className="flex items-center gap-2">
                {t("important_dates.events.title")}
                {orgEvents.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-200 text-slate-700 flex-shrink-0">
                    {orgEvents.length}
                  </span>
                )}
              </span>
            }
            description={t("important_dates.events.description")}
            action={
              <button
                type="button"
                onClick={() => setEventsExpanded((v) => !v)}
                aria-expanded={eventsExpanded}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
              >
                {eventsExpanded ? t("important_dates.events.hide") : t("important_dates.events.show")}
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    eventsExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>
            }
          >
            {!eventsExpanded ? null : isLoadingEvents ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
              </div>
            ) : orgEvents.length === 0 ? (
              <p className="text-sm text-slate-400 mb-3">{t("important_dates.events.none")}</p>
            ) : (
              <div className="space-y-2 mb-4">
                {currentEvents.length === 0 && (
                  <p className="text-sm text-slate-400">
                    {t("important_dates.events.none_current")}
                  </p>
                )}
                {currentEvents.map((event) => {
                  // A null override means "inherit the org default", which is
                  // how the feed column resolves it too — showing a bare false
                  // here would misreport events that inherit an org-level on.
                  const useNominations =
                    event.use_nominations ?? importantDates.eventsUseNominations;
                  return (
                    <div
                      key={event.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-2xl border border-slate-200 bg-white"
                    >
                      <div className="min-w-0">
                        <div className="text-sm sm:text-base font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                          <span className="truncate">{event.name}</span>
                          {event.source === "workvivo" && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 flex-shrink-0">
                              Workvivo
                            </span>
                          )}
                          {event.campaign_id && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-purple-700 flex-shrink-0">
                              {t("important_dates.events.campaign_linked")}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 break-words">
                          {eventDate(event.event_date)}
                          {event.end_date ? ` → ${eventDate(event.end_date)}` : ""}
                          {event.description ? ` · ${event.description}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                        <div
                          className="flex items-center gap-1.5"
                          title={
                            useNominations
                              ? t("important_dates.events.uses_nominations_title")
                              : t("important_dates.events.uses_celebration_title")
                          }
                        >
                          <span className="text-xs text-slate-500">{t("important_dates.events.nominations")}</span>
                          <PillToggle
                            checked={useNominations}
                            disabled={toggleEventNominationsMutation.isPending}
                            onChange={() =>
                              toggleEventNominationsMutation.mutate({
                                eventId: event.id,
                                useNominations: !useNominations,
                              })
                            }
                          />
                        </div>
                        <button
                          onClick={() => deleteEventMutation.mutate(event.id)}
                          disabled={deleteEventMutation.isPending}
                          className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title={t("important_dates.events.delete")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {pastEvents.length > 0 && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => setShowPastEvents((v) => !v)}
                      aria-expanded={showPastEvents}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                    >
                      <span className="min-w-0">
                        <span className="text-sm font-medium text-slate-700">
                          {t("important_dates.events.past_title")}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {t("important_dates.events.past_summary", {
                            count: importantDates.pastDays,
                            events: pastEvents.length,
                          })}
                        </span>
                      </span>
                      <span className="flex items-center gap-2 flex-shrink-0">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-200 text-slate-700">
                          {pastEvents.length}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 text-slate-500 transition-transform ${
                            showPastEvents ? "rotate-180" : ""
                          }`}
                        />
                      </span>
                    </button>

                    {showPastEvents && (
                      <div className="space-y-2 mt-2">
                        {pastEvents.map((event) => {
                          const prune = pruneLabel(
                            event,
                            importantDates.eventRetentionDays,
                            t
                          );
                          return (
                            <div
                              key={event.id}
                              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-2xl border border-slate-200 bg-slate-50/60"
                            >
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-600 flex items-center gap-2 flex-wrap">
                                  <span className="truncate">{event.name}</span>
                                  {event.source === "workvivo" && (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 flex-shrink-0">
                                      Workvivo
                                    </span>
                                  )}
                                  {event.campaign_id && (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-purple-700 flex-shrink-0">
                                      {t("important_dates.events.campaign_linked")}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500 break-words">
                                  {eventDate(event.event_date)}
                                  {event.end_date ? ` → ${eventDate(event.end_date)}` : ""}
                                  {prune ? ` · ${prune}` : ""}
                                </div>
                              </div>
                              <button
                                onClick={() => deleteEventMutation.mutate(event.id)}
                                disabled={deleteEventMutation.isPending}
                                className="self-end sm:self-auto p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                                title={t("important_dates.events.delete")}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Add event form */}
            {eventsExpanded && (
            <div className="p-3 sm:p-4 rounded-2xl border border-dashed border-slate-300 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={newEvent.name}
                  onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                  placeholder={t("important_dates.events.name_placeholder")}
                  maxLength={255}
                  className="w-full px-4 py-2 text-sm border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <input
                  type="text"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  placeholder={t("important_dates.events.description_placeholder")}
                  maxLength={1000}
                  className="w-full px-4 py-2 text-sm border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div>
                  <label className="block text-xs text-slate-500 mb-1">{t("important_dates.events.date_label")}</label>
                  <input
                    type="date"
                    value={newEvent.event_date}
                    onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                    className="w-full px-4 py-2 text-sm border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">{t("important_dates.events.end_date_label")}</label>
                  <input
                    type="date"
                    value={newEvent.end_date}
                    onChange={(e) => setNewEvent({ ...newEvent, end_date: e.target.value })}
                    className="w-full px-4 py-2 text-sm border border-slate-300 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
              <button
                onClick={handleCreateEvent}
                disabled={createEventMutation.isPending}
                className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm org-bg-primary org-btn text-white rounded-full transition-colors disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                {createEventMutation.isPending ? t("important_dates.events.adding") : t("important_dates.events.add")}
              </button>
            </div>
            )}
          </AdminSectionCard>
      </div>

      {editingOccasion && (
        <OccasionStyleModal
          type={editingOccasion}
          saving={updateOccasionMutation.isPending}
          selectedOrgId={selectedOrgId}
          onClose={() => setEditingOccasion(null)}
          onSave={({ emoji, colorFrom, colorTo }) =>
            updateOccasionMutation.mutate({
              type: editingOccasion,
              emoji,
              colorFrom,
              colorTo,
            })
          }
        />
      )}
    </div>
  );
};

export default ImportantDatesSection;
